import { emptyGrnLine, type DraftGrnLine } from './GrnLineEditor';
import type { ExtractedInvoiceLine } from '../types';

/**
 * Turns what AI vision read off a vendor invoice into draft GRN lines for
 * review. medicine_id is always left null — a product name read off a
 * photo is text, not a resolved catalog id, so every line still needs the
 * same explicit MedicinePicker search-and-pick/"Declare new brand" step any
 * hand-typed GRN line requires (see GrnLineEditor's source_hint, which shows
 * the extracted text next to the picker).
 */
export function linesFromExtractedInvoice(lines: ExtractedInvoiceLine[], gstPercentageOptions: number[]): DraftGrnLine[] {
  return lines.map((line) => {
    const draft: DraftGrnLine = {
      ...emptyGrnLine(gstPercentageOptions),
      source_hint: [line.manufacturer, line.product_name].filter(Boolean).join(' — ') || line.product_name,
      hsn_code: line.hsn_code || '',
      batch_number: line.batch_number || '',
      expiry_date: line.expiry_date || '',
      mrp: line.mrp != null ? String(line.mrp) : '',
      unit_cost: line.rate != null ? String(line.rate) : '',
      discount_percent: line.discount_percent != null ? String(line.discount_percent) : '',
      units_per_pack: line.pack_units != null ? String(line.pack_units) : '',
      strips_received: line.quantity != null ? String(line.quantity) : '',
      free_strips: line.free_quantity != null ? String(line.free_quantity) : '',
      line_amount: line.amount != null ? String(line.amount) : '',
      mismatch_note: line.amount_mismatch ? line.mismatch_note : undefined,
    };
    if (line.gst_percent != null) draft.gst_percent = String(line.gst_percent);

    // Mirror GrnLineEditor's own strips×units-per-pack auto-fill so the
    // review form opens with a sensible quantity already computed, not blank.
    const strips = parseInt(draft.strips_received, 10);
    const free = parseInt(draft.free_strips, 10) || 0;
    const perPack = parseInt(draft.units_per_pack, 10);
    if (!Number.isNaN(strips) && !Number.isNaN(perPack)) {
      draft.quantity_received = String((strips + free) * perPack);
      draft.invoiced_quantity = draft.quantity_received; // the invoice's count; edit received if the shelf count differs
    }

    return draft;
  });
}
